import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";

import useStyles from "@hooks/useStyles";
import useConstants from "@hooks/useConstants";
import useStores from "@hooks/useStores";
import { useLocation } from "react-router-dom";

import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";

import TextField, {
	ZipcodeTextField,
} from "@components/Global/Custom/TextField";
import Autocomplete from "@components/Global/Custom/Autocomplete";
import Chip from "@material-ui/core/Chip";
import useStaticContext from "@hooks/useStaticContext";

const { BRAND_NAME, SITE_NAME } = process.env;

const ProBanner = observer(({ title, subtitle, title2 }) => {
	const {
		h1,
		h1_full,
		h2,
		cta,
		serviceInputPlaceholder,
		backgroundImage,
		showChip,
		alt,
	} = useConstants("ProBannerViewModel");

	const { proStore: store } = useStores();
	const { mobile, desktop } = backgroundImage;
	const classes = useStyles("Pro");

	const serviceOnSelectHandler = (service) => store.update("service", service);
	const zipcodeOnChangeHandler = (e) => store.update("zipcode", e.target.value);
	const getStartedHandler = (groupId) => (e) => {
		e.preventDefault();
		store.showSignupFunnel(groupId);
	};

	const {
		app,
		serviceName: serviceVar,
		title: titleCustom,
	} = useStaticContext();

	const { city, state } = app.location || {};

	const heroPictureMobile = store.mobileHeroImage;
	const heroPicture = store.heroImage;

	return (
		<React.Fragment>
			<Box
				component="section"
				className={classes.hero}
				height={{ xs: 220, sm: "calc(100vh - 65px)" }}
			>
				<picture className={classes.picture}>
					<source
						srcSet={`${serviceVar ? heroPictureMobile : mobile} 300w, ${
							serviceVar ? heroPictureMobile : mobile
						} 768w, ${serviceVar ? heroPicture : desktop} 1280w, ${
							serviceVar ? heroPicture : desktop
						} 3200w`}
						sizes="100vw"
					/>
					<img
						sizes="100vw"
						srcSet={`${serviceVar ? heroPictureMobile : mobile} 300w, ${
							serviceVar ? heroPictureMobile : mobile
						} 768w, ${serviceVar ? heroPicture : desktop} 1280w, ${
							serviceVar ? heroPicture : desktop
						} 3200w`}
						src={desktop}
						alt={alt}
						className={classes.backgroundImage}
						loading="lazy"
						decoding="async"
					/>
				</picture>

				<Box
					height="100%"
					display={{ xs: "flex", sm: "none" }}
					alignItems="flex-end"
					position="relative"
					color="#FFF"
					py={4}
					px={4}
					className={classes.pictureGradient}
				>
					<Box display={"flex"} flexDirection={"column"}>
						{showChip && city && (
							<Box mb={2}>
								<Chip
									label={`${city}, ${state}`}
									style={{ opacity: 0.6, fontWeight: 700 }}
								/>
							</Box>
						)}

						<h1 className={classes.joinH1}>
							{titleCustom ? (
								titleCustom
							) : title2 ? (
								title2
							) : h1_full ? (
								h1_full
							) : (
								<>{`Get ${title ? title : h1}${
									city ? " in " + city + "." : " in your area."
								}`}</>
							)}
						</h1>
					</Box>
				</Box>

				<Box
					width="100%"
					left={0}
					top="50%"
					pr={2}
					pl={2}
					className={classes.heroBox}
				>
					<Container maxWidth={false} className={classes.proContainer}>
						<Box
							display={{ xs: "none", sm: "block" }}
							borderRadius={12}
							boxShadow={5}
							bgcolor="#FFF"
							className={classes.heroBoxContent}
							maxWidth={480}
						>
							{showChip && city && (
								<Box mb={2}>
									<Chip
										label={`${city}, ${state}`}
										style={{ opacity: 0.6, fontWeight: 700 }}
									/>
								</Box>
							)}

							<h1 className={classes.joinH1}>
								{titleCustom ? (
									titleCustom
								) : title2 ? (
									title2
								) : h1_full ? (
									h1_full
								) : (
									<>
										{city ? (
											<>
												{`Get ${title ? title : h1}${
													city ? " in " + city + "." : " in your area."
												}`}
											</>
										) : (
											<>{`Get ${title ? title : h1}.`}</>
										)}
									</>
								)}
							</h1>

							<div className={classes.joinSubText}>
								{titleCustom
									? `Find ${titleCustom.toLowerCase()} that fit your needs. New jobs posted every minute - don't miss out.`
									: subtitle
									? subtitle
									: h2}
							</div>

							<form onSubmit={getStartedHandler("desktop")}>
								<Grid container spacing={3}>
									<Grid item xs={12}>
										<Autocomplete
											onSelect={serviceOnSelectHandler}
											searchOptions={{ active: true, midtail: false }}
											id="service"
											autoHighlight
											autoSelect
											defaultValue={
												store.service?.name_proper || store.service?.name || ""
											}
											TextFieldProps={{
												groupId: "desktop",
												InputProps: {
													classes: {
														adornedStart: classes.autocompleteAdornedStart,
														input: classes.autocompleteInputRoot,
													},
												},
												inputProps: {
													"autoCorrect": "off",
													"spellCheck": "false",
													"aria-label": "Enter service",
												},
												placeholder: serviceInputPlaceholder,
												variant: "outlined",
											}}
										/>
									</Grid>

									<Grid item xs={12}>
										<ZipcodeTextField
											groupId={"desktop"}
											variant={"outlined"}
											fullWidth
											onChange={zipcodeOnChangeHandler}
											defaultValue={store.zipcode}
											id="zip"
										/>
									</Grid>

									<Grid item xs={12}>
										<Box pt={1} pb={2}>
											<div className={classes.leadCount}>10,000+</div>
											<div className={classes.leadText}>
												leads on {BRAND_NAME} per month
											</div>
										</Box>
									</Grid>

									<Grid item xs={12}>
										<Button
											type={"submit"}
											fullWidth
											size={"large"}
											variant="contained"
											color="primary"
											disableElevation
											className={classes.joinButton}
										>
											{cta}
										</Button>
									</Grid>
								</Grid>
							</form>
						</Box>
					</Container>
				</Box>
			</Box>

			<Box
				display={{ xs: "block", sm: "none" }}
				p={4}
				maxWidth="100%"
				bgcolor="#FFF"
			>
				<div className={classes.joinSubText}>
					{titleCustom
						? `Find ${titleCustom.toLowerCase()} that fit your needs. New jobs posted every minute - don't miss out.`
						: subtitle
						? subtitle
						: h2}
				</div>

				<form onSubmit={getStartedHandler("mobile")}>
					<Grid container spacing={4}>
						<Grid item xs={12}>
							<Autocomplete
								onSelect={serviceOnSelectHandler}
								autoHighlight
								autoSelect
								id="service-mobile"
								defaultValue={
									store.service?.name_proper || store.service?.name || ""
								}
								searchOptions={{ midtail: false, active: true }}
								TextFieldProps={{
									groupId: "mobile",
									inputProps: {
										"autoCorrect": "off",
										"spellCheck": "false",
										"aria-label": "Enter service",
									},
									InputProps: {
										classes: {
											adornedStart: classes.autocompleteAdornedStart,
											input: classes.autocompleteInputRoot,
										},
									},
									placeholder: serviceInputPlaceholder,
									variant: "outlined",
								}}
							/>
						</Grid>

						<Grid item xs={12}>
							<TextField
								groupId={"mobile"}
								type={"zipcode"}
								variant={"outlined"}
								fullWidth
								onChange={zipcodeOnChangeHandler}
								defaultValue={store.zipcode}
								id="zip-mobile"
							/>
						</Grid>

						<Grid item xs={12}>
							<Box pt={1} pb={1}>
								<div className={classes.leadCount}>10,000+</div>
								<div className={classes.leadText}>
									leads on {BRAND_NAME} per month
								</div>
							</Box>
						</Grid>

						<Grid item xs={12}>
							<Button
								fullWidth
								type={"submit"}
								size={"large"}
								variant="contained"
								color="primary"
								disableElevation
								className={classes.joinButton}
							>
								{cta}
							</Button>
						</Grid>
					</Grid>
				</form>

				<Box pt={7}>
					<Divider light={true} />
				</Box>
			</Box>
		</React.Fragment>
	);
});

ProBanner.propTypes = {
	selectedServiceName: PropTypes.string,
	city: PropTypes.string,
	title: PropTypes.string,
	subtitle: PropTypes.string,
	title2: PropTypes.string,
};

export default ProBanner;
